import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, Input, Label } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useForm, Controller } from "react-hook-form";
import { Pencil, AlertCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { addDoctor } from "./service/doctor-service";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import SignatureCanvas from "react-signature-canvas";
import S3ImageLoader from "../../S3ImageLoader";

const SignaturePreview = ({ src, alt }) => {
    const { t } = useTranslation();
    if (!src) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ width: "100%", height: "160px" }}
            >
                <span className="text-muted">{t("No Signature Preview")}</span>
            </div>
        );
    }

    if (src.startsWith("data:") || src.startsWith("blob:")) {
        return (
            <img
                src={src}
                alt={alt}
                style={{ width: "70%", height: "100px", objectFit: "contain" }}
            />
        );
    }

    return (
        <S3ImageLoader
            fileName={src}
            fallbackSrc=""
            alt={alt}
            style={{ width: "70%", height: "100px", objectFit: "contain" }}
        />
    );
};

export default function AddDoctor() {
    const { t } = useTranslation();
    const [profileImage, setProfileImage] = useState(null);
    const [imageError, setImageError] = useState("");       // This will now hold the preview (Base64)
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [signatureMode, setSignatureMode] = useState("draw");
    const [signatureName, setSignatureName] = useState("");
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const signRef = useRef(null);
    const signatureFileRef = useRef(null);
    const { setLoading } = useLoader();
    const navigate = useNavigate()

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting },
        control
    } = useForm();
    const reportDegree = watch("signatureQualification");

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // File size check
        if (file.size > 5 * 1024 * 1024) {
            setImageError(t("File size must be less than 5MB"));
            return;
        }

        // File type check
        if (!["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)) {
            setImageError(t("Only JPG, PNG, or GIF files are allowed"));
            return;
        }

        setImageError("");

        // Store the actual File object
        setProfileImageFile(file);

        // Create preview (Base64) - only for UI
        const reader = new FileReader();
        reader.onloadend = () => {
            setProfileImage(reader.result); // Base64 string for preview
        };
        reader.readAsDataURL(file);
    };

    const clearSignature = () => {
        if (signRef.current) signRef.current.clear();
        setSignaturePreview(null);
        setSignatureFile(null);
        if (signatureFileRef.current) signatureFileRef.current.value = "";
    };

    const updateCanvasPreview = () => {
        if (!signRef.current || signRef.current.isEmpty()) {
            setSignaturePreview(null);
            return;
        }
        setSignaturePreview(signRef.current.toDataURL("image/png"));
    };

    const handleSignatureMode = (mode) => {
        setSignatureMode(mode);
        clearSignature();
    };

    const handleSignatureUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.warning(t("Signature file size must be less than 5MB"));
            return;
        }

        if (!file.type.startsWith("image/")) {
            toast.warning(t("Only image files are allowed for signature"));
            return;
        }

        setSignatureFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setSignaturePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const getSignatureFile = async () => {
        if (signatureMode === "draw") {
            if (!signRef.current || signRef.current.isEmpty()) return null;
            const dataURL = signRef.current.toDataURL("image/png");
            const blob = await (await fetch(dataURL)).blob();
            return new File([blob], "signature.png", { type: "image/png" });
        }
        return signatureFile;
    };

    const onSubmit = async (data) => {
        setLoading(true)
        try {
            const form_data = new FormData();
            form_data.append("fname", data.firstName);
            form_data.append("lname", data.lastName);
            form_data.append("email", data.email);
            form_data.append("mobileNumber", data.mobile);
            form_data.append("degree", data.degree);
            form_data.append("hospitalName", data.hospital);
            form_data.append("commissionPercent", data.commision);
            form_data.append("address", data.address);
            if (profileImageFile) {
                form_data.append("profilePicture", profileImageFile);
            }
            form_data.append("signatureName", signatureName || "");
            form_data.append("signatureQualification", data.signatureQualification || "");
            const signature = await getSignatureFile();
            if (signature) {
                form_data.append("signature", signature);
            }
            const res = await addDoctor(form_data);
            if (res.data.success == true) {
                toast.success(res.data.message);
                navigate(`${process.env.PUBLIC_URL}/doctors-list`)
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
        // console.log("Form submitted:", { ...data, profileImage });
        // alert("Doctor added successfully!");
        reset();
        setProfileImage(null);
        setSignatureName("");
        clearSignature();
    };

    return (
        <>
            <Breadcrumbs mainTitle={t("Add Doctor")} parent={t("Home")} subParent={t("Doctor List")} title={t("Add Doctor")} />

            <Container fluid={true}>
                <Row className="tw-flex tw-justify-center">
                    <Col md="12" lg="12">
                        <Card className="tw-shadow-xl tw-rounded-2xl tw-border-0 tw-bg-white tw-p-6 md:tw-p-8">
                            {/* Form */}
                            <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-4">
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-x-4 tw-gap-y-3">
                                    {/* Row 1: First Name */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("First Name")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t("Enter first name")}
                                            {...register("firstName", { required: t("First name is required") })}
                                        />
                                        {errors.firstName && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.firstName.message}</p>
                                        )}
                                    </div>

                                    {/* Row 1: Last Name */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("Last Name")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t("Enter last name")}
                                            {...register("lastName", { required: t("Last name is required") })}
                                        />
                                        {errors.lastName && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.lastName.message}</p>
                                        )}
                                    </div>

                                    {/* Row 1: Email */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("Email")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder={t("Enter email address")}
                                            {...register("email", {
                                                required: t("Email is required"),
                                                pattern: {
                                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                    message: t("Enter a valid email address"),
                                                },
                                            })}
                                        />
                                        {errors.email && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email.message}</p>
                                        )}
                                    </div>

                                    {/* Row 2: Mobile Number */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("Mobile Number")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <Controller
                                            name="mobile"
                                            control={control}
                                            rules={{
                                                required: t("Mobile is required"),
                                                minLength: {
                                                    value: 10,
                                                    message: t("Invalid mobile number"),
                                                },
                                            }}
                                            render={({ field }) => (
                                                <PhoneInput
                                                    {...field}
                                                    country="in"
                                                    enableSearch
                                                    placeholder="+91 9876543210"
                                                    inputClass="form-control"
                                                    inputStyle={{ width: "100%" }}
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            )}
                                        />
                                        {/* <input
                                                    type="tel"
                                                    maxLength="10"
                                                    className="form-control"
                                                    placeholder="Enter mobile number"
                                                    {...register("mobile", {
                                                        required: "Mobile number is required",
                                                        pattern: {
                                                            value: /^[0-9]{10}$/,
                                                            message: "Enter a valid 10-digit number",
                                                        },
                                                    })}
                                                /> */}
                                        {errors.mobile && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.mobile.message}</p>
                                        )}
                                    </div>

                                    {/* Row 2: Degree (Optional) */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">{t("Qualification (Optional)")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t("Enter degree")}
                                            {...register("degree")}
                                        />
                                    </div>

                                    {/* Row 2: Hospital */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("Hospital")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t("Enter hospital")}
                                            {...register("hospital", {
                                                required: t("Hospital is required"),
                                            })}
                                        />
                                        {errors.hospital && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.hospital.message}</p>
                                        )}
                                    </div>

                                    {/* Row 3: Commission (%) */}
                                    <div>
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("Commission (%)")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder={t("Enter commission")}
                                            {...register("commision", {
                                                required: t("Commision is required"),
                                            })}
                                        />
                                        {errors.commision && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.commision.message}</p>
                                        )}
                                    </div>

                                    {/* Row 3: Address (1 column on 2-col md layout, spans remaining 2 columns on lg 3-col layout) */}
                                    <div className="lg:tw-col-span-2">
                                        <label className="tw-block tw-font-medium tw-mb-1">
                                            {t("Address")} <span className="tw-text-red-500">*</span>
                                        </label>
                                        <textarea
                                            rows="1"
                                            className="form-control"
                                            placeholder={t("Enter full address")}
                                            {...register("address", { required: t("Address is required") })}
                                        />
                                        {errors.address && (
                                            <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.address.message}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Profile Image & Signature Section */}
                                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
                                    {/* Column 1: Profile Image (compact version of original centered UI) */}
                                    <div className="lg:tw-col-span-1">
                                        <div className="border rounded p-3 tw-h-full tw-flex tw-flex-col">
                                            <label className="tw-block tw-font-medium tw-mb-2">
                                                {t("Profile Image")}
                                            </label>
                                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-my-auto tw-py-3">
                                                <div className="tw-relative">
                                                    <label htmlFor="profile-upload" className="tw-cursor-pointer tw-mb-0">
                                                        <div className="tw-w-28 tw-h-28 tw-rounded-full tw-overflow-hidden tw-bg-gray-100 tw-border tw-border-gray-300 tw-flex tw-items-center tw-justify-center hover:tw-ring-2 hover:bg-primary tw-transition-all">
                                                            {profileImage ? (
                                                                <img
                                                                    src={profileImage}
                                                                    alt={t("Profile")}
                                                                    className="tw-w-full tw-h-full tw-object-cover"
                                                                />
                                                            ) : (
                                                                <span className="tw-text-gray-400 tw-text-sm">{t("Upload Image")}</span>
                                                            )}
                                                        </div>
                                                    </label>
                                                    <label
                                                        htmlFor="profile-upload"
                                                        className="tw-absolute tw-bottom-1 tw-right-1 bg-primary tw-rounded-full tw-p-2 tw-shadow-md tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-border-2 tw-border-white tw-mb-0"
                                                    >
                                                        <Pencil className="tw-w-3.5 tw-h-3.5 tw-text-white" />
                                                    </label>
                                                    <input
                                                        id="profile-upload"
                                                        type="file"
                                                        accept="image/jpeg,image/jpg,image/png,image/gif"
                                                        onChange={handleImageUpload}
                                                        className="tw-hidden"
                                                    />
                                                </div>
                                                {imageError && (
                                                    <div className="tw-flex tw-items-center tw-gap-1 tw-text-red-600 tw-text-xs tw-mt-2">
                                                        <AlertCircle className="tw-w-3.5 tw-h-3.5" />
                                                        <span>{imageError}</span>
                                                    </div>
                                                )}
                                                <p className="tw-text-gray-500 tw-text-xs tw-mt-2 tw-mb-0 text-center">
                                                    {t("JPG, PNG or GIF. Max 5MB")}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Column 2: Signature */}
                                    <div className="lg:tw-col-span-2">
                                        <div className="border rounded p-3 tw-h-full">
                                            <label className="tw-block tw-font-medium tw-mb-2">{t("Signature")}</label>
                                            <div className="d-flex tw-space-x-4 mb-3">
                                                <div className="radio radio-primary">
                                                    <Input
                                                        id="doctor-signature-draw"
                                                        type="radio"
                                                        name="doctorSignatureMode"
                                                        checked={signatureMode === "draw"}
                                                        onChange={() => handleSignatureMode("draw")}
                                                    />
                                                    <Label for="doctor-signature-draw" className="mb-0 pe-2">{t("Manual")}</Label>
                                                </div>
                                                <div className="radio radio-primary">
                                                    <Input
                                                        id="doctor-signature-upload"
                                                        type="radio"
                                                        name="doctorSignatureMode"
                                                        checked={signatureMode === "upload"}
                                                        onChange={() => handleSignatureMode("upload")}
                                                    />
                                                    <Label for="doctor-signature-upload" className="mb-0 pe-2">{t("Upload Image")}</Label>
                                                </div>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    {signatureMode === "draw" ? (
                                                        <div className="border rounded p-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <p className="fw-bold mb-0">{t("Draw Signature")}</p>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary px-2 py-0"
                                                                    onClick={clearSignature}
                                                                >
                                                                    {t("Clear")}
                                                                </button>
                                                            </div>
                                                            <SignatureCanvas
                                                                ref={signRef}
                                                                penColor="black"
                                                                onEnd={updateCanvasPreview}
                                                                canvasProps={{
                                                                    className: "border w-100 rounded",
                                                                    style: { height: "120px" },
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="border rounded p-3 text-center">
                                                            <p className="fw-bold mb-2">{t("Upload Signature Image")}</p>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                ref={signatureFileRef}
                                                                style={{ display: "none" }}
                                                                onChange={handleSignatureUpload}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={() => signatureFileRef.current?.click()}
                                                            >
                                                                {t("Upload")}
                                                            </button>
                                                            {signatureFile ? (
                                                                <p className="text-muted small mt-2 mb-0">{signatureFile.name}</p>
                                                            ) : null}
                                                        </div>
                                                    )}

                                                    <div className="mt-3">
                                                        <label className="tw-block tw-font-medium tw-mb-1">
                                                            {t("Signature Name On Lab Report")}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t("Enter signature name")}
                                                            value={signatureName}
                                                            onChange={(e) => setSignatureName(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="tw-block tw-font-medium tw-mb-1">
                                                            {t("Qualification On Lab Report")}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t("Enter degree name")}
                                                            {...register("signatureQualification")}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="border rounded p-3 text-center h-100">
                                                        <h5 className="fw-bold mb-3">{t("Preview")}</h5>
                                                        <SignaturePreview src={signaturePreview} alt={t("Signature preview")} />
                                                        <p className="fw-bold mt-3 mb-0">
                                                            {signatureName || t("No Name Entered")}
                                                        </p>
                                                        {reportDegree ? (
                                                            <p className="mb-0">{reportDegree}</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="text-end tw-mt-4">
                                    <button
                                        type="submit"
                                        // disabled={isSubmitting}
                                        className="btn btn-primary me-3"
                                    >
                                        {isSubmitting ? t("Submitting...") : t("Add Doctor")}
                                    </button>
                                    <Link to={`${process.env.PUBLIC_URL}/doctors-list`}>
                                        <button
                                            className="btn btn-secondary"
                                        >
                                            {t("Cancel")}
                                        </button>
                                    </Link>
                                </div>
                            </form>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
